import { useMyBrokerage } from 'data/brokerage';
import { Field, Form, Formik } from 'formik';
import React from 'react';
import { toast } from 'sonner';
import * as Yup from 'yup';

import Dialog from '@material-ui/core/Dialog';
import { makeStyles } from '@material-ui/core/styles';

import Button from 'creative-components/CustomButtons/Button';
import CustomInputDashboard from 'creative-components/CustomInput/CustomInputDashboard';

import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import SimpleModalHeader from 'components/SimpleModalHeader/SimpleModalHeader';

import analyticsClient from 'utils/analytics';
import { addBrokerageAdmin } from 'utils/api';
import { nameRegExp, phoneRegExp, showAPIErrorAlert } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  buttonContainer: {
    textAlign: 'center',
    margin: '30px 0 0',
  },
  title: {
    '& > span': {
      color: theme.palette.orange.main,
    },
  },
}));

const AddBrokerageAdminModal = ({ onClose }) => {
  const classes = useStyles();
  const { mutateBrokerage } = useMyBrokerage();

  return (
    <Formik
      initialValues={{
        email: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
      }}
      onSubmit={async ({
        email, firstName, lastName, phoneNumber,
      }) => {
        try {
          const { user } = await addBrokerageAdmin(email, firstName, lastName, phoneNumber);

          mutateBrokerage();

          onClose();

          toast.success(`${firstName} ${lastName} has been emailed an invite link!`);

          // Track GTM event
          analyticsClient.track('user_registration_started', {
            agentdata_user_id: user._id,
            email: user.email,
            role: user.role.name,
          });
        } catch (err) {
          console.error('error', err);
          showAPIErrorAlert(err);
        }
      }}
      validationSchema={Yup.object().shape({
        email: Yup.string().email().required(),
        firstName: Yup.string().required().matches(nameRegExp),
        lastName: Yup.string().required().matches(nameRegExp),
        phoneNumber: Yup.string().required().matches(phoneRegExp, 'Phone number is not valid'),
      })}
    >
      {(props) => {
        const {
          touched, errors, dirty, isValid, handleSubmit, isSubmitting, setFieldValue,
        } = props;

        return (
          <Form>
            <Dialog
              open
              scroll="body"
              keepMounted
              maxWidth="sm"
              onClose={onClose}
            >
              <SimpleModalHeader
                onClose={onClose}
                title={(
                  <div className={classes.title}>
                    Add New
                    {' '}
                    <span>Admin</span>
                  </div>
                )}
              />

              {isSubmitting ? <LoadingIndicator modal /> : null}

              <Field
                name="firstName"
              >
                {({ field }) => (
                  <CustomInputDashboard
                    labelText="First Name"
                    inputProps={{
                      ...field,
                      autoComplete: 'given-name',
                      maxLength: 50,
                    }}
                    error={touched[field.name] && errors[field.name] !== undefined}
                  />
                )}
              </Field>
              <Field
                name="lastName"
              >
                {({ field }) => (
                  <CustomInputDashboard
                    labelText="Last Name"
                    inputProps={{
                      ...field,
                      autoComplete: 'family-name',
                      maxLength: 50,
                    }}
                    error={touched[field.name] && errors[field.name] !== undefined}
                  />
                )}
              </Field>
              <Field
                name="email"
              >
                {({ field }) => (
                  <CustomInputDashboard
                    labelText="Email"
                    inputProps={{
                      ...field,
                      autoComplete: 'email',
                    }}
                    error={touched[field.name] && errors[field.name] !== undefined}
                  />
                )}
              </Field>
              <Field
                name="phoneNumber"
              >
                {({ field }) => (
                  <CustomInputDashboard
                    labelText="Phone Number"
                    inputProps={{
                      ...field,
                      type: 'tel',
                      autoComplete: 'tel',
                    }}
                    mask={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                    error={touched[field.name] && errors[field.name] !== undefined}
                  />
                )}
              </Field>

              <div className={classes.buttonContainer}>
                <Button
                  round
                  color="primary"
                  disabled={!isValid || !dirty || isSubmitting}
                  onClick={handleSubmit}
                >
                  Add & Send Registration Email
                </Button>
              </div>
            </Dialog>
          </Form>
        );
      }}
    </Formik>
  );
};

export default AddBrokerageAdminModal;
